import { create } from 'zustand';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { callEdgeFunction } from '../lib/edgeFunctions';
import type { DemandeLue, NiveauAcces, ResultatDeDemande } from '../lib/inclusion/demandes';

/* Les types générés de Supabase sont périmés (colonnes récentes absentes) : lectures et écritures nouvelles non typées. */
const sansTypes = () => supabase as unknown as SupabaseClient;

/** Ce que la fiche d'inclusion dit de la décision de l'équipe. */
export interface DecisionDeLEquipe {
  has_needs: boolean | null;
  /** `null` : pas encore examiné. */
  niveau_acces: NiveauAcces | null;
  niveau_acces_le: string | null;
  niveau_acces_motif: string | null;
}

export interface InclusionRequest {
  id: string;
  user_id: string;
  kid_id: string;
  activity_id: string;
  request_date: string;
  status: 'pending' | 'approved' | 'rejected' | 'converted' | 'archived';
  inclusion_details: any;
  admin_notes?: string;
  /** Quand le parent a été prévenu de la décision ; `null` : pas encore (ou le courriel a échoué). */
  reponse_envoyee_le?: string | null;
  raison_refus?: 'securite' | 'place' | null;
  mot_au_parent?: string | null;
  // Joined data
  kid?: {
    prenom: string;
    nom: string;
    /** Un objet (une fiche par enfant) ; un tableau selon la version de PostgREST. */
    inclusion?: DecisionDeLEquipe | DecisionDeLEquipe[] | null;
  };
  parent?: {
    prenom: string;
    nom: string;
    email: string;
    telephone: string;
  };
  session?: {
    stage: {
      title: string;
    };
    start_date: string;
    end_date: string;
    periode?: string | null;
    capacity?: number | null;
    current_registrations?: number | null;
    visible_from?: string | null;
    center: {
      name: string;
    };
  };
}

interface InclusionRequestState {
  requests: InclusionRequest[];
  /** Les inscriptions vivantes des enfants qui ont des demandes : `enfant|semaine`. */
  inscriptionsVivantes: string[];
  /** Les demandes du parent connecté, pour la page des stages. */
  mesDemandes: DemandeLue[];
  isLoading: boolean;
  error: string | null;
  fetchRequests: () => Promise<void>;
  chargerMesDemandes: () => Promise<void>;
  envoyerDemande: (kidId: string, sessionIds: string[]) => Promise<ResultatDeDemande>;
  updateRequestStatus: (id: string, status: InclusionRequest['status'], adminNotes?: string) => Promise<void>;
  /** Rend les demandes réellement décidées (celles qui étaient encore en attente). */
  deciderLesDemandes: (
    ids: string[], status: 'converted' | 'rejected' | 'archived',
    reponse?: { raison_refus?: 'securite' | 'place'; mot_au_parent?: string | null },
  ) => Promise<string[]>;
  prevenirLeParent: (ids: string[]) => Promise<{ envoye: boolean; deja?: boolean }>;
  poserNiveau: (kidId: string, niveau: NiveauAcces, motif: string | null) => Promise<void>;
}

export const useInclusionRequestStore = create<InclusionRequestState>((set, get) => ({
  requests: [],
  inscriptionsVivantes: [],
  mesDemandes: [],
  isLoading: false,
  error: null,

  fetchRequests: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await sansTypes()
        .from('inclusion_requests')
        .select(`
          *,
          kid:kid_id(
            prenom,
            nom,
            inclusion:kid_inclusion(
              has_needs,
              niveau_acces,
              niveau_acces_le,
              niveau_acces_motif
            )
          ),
          parent:user_id(
            prenom,
            nom,
            email,
            telephone
          ),
          session:activity_id(
            stage:stage_id(
              title
            ),
            start_date,
            end_date,
            periode,
            capacity,
            current_registrations,
            visible_from,
            center:center_id(
              name
            )
          )
        `)
        .order('request_date', { ascending: false });

      if (error) throw error;

      /*
        Les semaines où l'enfant est déjà inscrit : l'inscription admin le
        faisait quand l'écran ne marchait plus, et la demande restait en attente.
      */
      const enfants = [...new Set(((data ?? []) as { kid_id: string }[]).map((d) => d.kid_id))];
      const { data: inscrits, error: errInscrits } = enfants.length === 0
        ? { data: [], error: null }
        : await sansTypes()
            .from('registrations')
            .select('kid_id, activity_id')
            .in('kid_id', enfants)
            .in('payment_status', ['paid', 'pending']);
      if (errInscrits) throw errInscrits;

      set({
        requests: data || [],
        inscriptionsVivantes: ((inscrits ?? []) as { kid_id: string; activity_id: string }[])
          .map((i) => `${i.kid_id}|${i.activity_id}`),
      });
    } catch (error: any) {
      console.error('Error fetching inclusion requests:', error);
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  /*
    Les siennes seulement : un administrateur qui a aussi des enfants lit sous
    RLS toutes les demandes, d'où le filtre explicite.
  */
  chargerMesDemandes: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      set({ mesDemandes: [] });
      return;
    }
    const { data, error } = await sansTypes()
      .from('inclusion_requests')
      .select('id, kid_id, activity_id, status, request_date')
      .eq('user_id', user.id);
    if (error) {
      console.error('Error fetching own inclusion requests:', error);
      return;
    }
    set({ mesDemandes: (data ?? []) as DemandeLue[] });
  },

  /*
    Une demande pour plusieurs semaines : la fonction Edge l'écrit au nom du
    parent (sans doublon, `demander_inclusion`) et prévient l'équipe par un
    seul courriel. L'ancien écrivait une ligne par clic, directement.
  */
  envoyerDemande: async (kidId, sessionIds) => {
    const resultat = await callEdgeFunction<ResultatDeDemande>('demander-inclusion', { kidId, sessionIds });
    await get().chargerMesDemandes();
    return resultat;
  },

  /* Une demande seule : les notes de l'administration. Accepter, refuser et classer passent par deciderLesDemandes. */
  updateRequestStatus: async (id, status, adminNotes) => {
    set({ isLoading: true, error: null });
    try {
      const updates: any = { status };
      if (adminNotes !== undefined) {
        updates.admin_notes = adminNotes;
      }

      const { error } = await supabase
        .from('inclusion_requests')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await get().fetchRequests();
    } catch (error: any) {
      console.error('Error updating inclusion request status:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  /*
    La décision vaut pour toutes les lignes d'une semaine, doublons compris, et
    seulement pour celles encore en attente : une ligne déjà décidée ne change
    pas sous un second clic.

    Accepter, c'est inscrire : l'écran commande d'abord pour la famille
    (`create_order`, comme l'inscription admin, qui crée l'inscription et la
    facture), puis passe ici les demandes en `converted`. L'ancien geste
    insérait lui-même la ligne d'inscription, au prix normal, sans facture —
    et la RLS le refuse depuis août 2026. La raison d'un refus et le mot de
    l'équipe s'écrivent avec le statut : le courriel au parent s'en déduit.

    Une mise à jour que la RLS ou une décision déjà prise écarte ne fait pas
    d'erreur, seulement zéro ligne — c'est ainsi que l'ancien « Approuver »
    échouait sans le dire. Les lignes réellement décidées sont donc relues, et
    aucune est une erreur.
  */
  deciderLesDemandes: async (ids, status, reponse = {}) => {
    if (ids.length === 0) return [];
    const { data, error } = await sansTypes()
      .from('inclusion_requests')
      .update({
        status,
        ...(reponse.raison_refus ? { raison_refus: reponse.raison_refus } : {}),
        ...(reponse.mot_au_parent?.trim() ? { mot_au_parent: reponse.mot_au_parent.trim() } : {}),
      })
      .in('id', ids)
      .eq('status', 'pending')
      .select('id');
    await get().fetchRequests();
    if (error) {
      console.error('Error deciding inclusion requests:', error);
      throw error;
    }
    const decidees = ((data ?? []) as { id: string }[]).map((d) => d.id);
    if (decidees.length === 0) throw new Error('Ces demandes ne sont plus en attente : la liste a été relue.');
    return decidees;
  },

  /*
    Le courriel au parent : la fonction Edge relit la décision en base et ne
    l'envoie qu'une fois. À rappeler sans crainte si le premier envoi a échoué.
  */
  prevenirLeParent: async (ids) => {
    const r = await callEdgeFunction<{ envoye: boolean; deja?: boolean }>('repondre-demande-inclusion', { demandes: ids });
    await get().fetchRequests();
    return r;
  },

  /*
    Le niveau d'accès : décision de l'équipe, qui ne touche pas aux besoins
    déclarés (rapport ONE, informations d'accueil). L'auteur et l'heure sont
    écrits par la base ; seule l'équipe le peut (`[niveau-acces-reserve]`).
  */
  poserNiveau: async (kidId, niveau, motif) => {
    const { data, error } = await sansTypes()
      .from('kid_inclusion')
      .update({ niveau_acces: niveau, niveau_acces_motif: motif })
      .eq('kid_id', kidId)
      .select('kid_id');
    if (error) throw error;
    if ((data ?? []).length === 0) throw new Error('La fiche d\'inclusion de l\'enfant est introuvable.');
    await get().fetchRequests();
  },
}));
